import React, { useState, useMemo } from "react";
import "./MetaLiftTestPower.scss";
import { Button as ButtonIcon, ButtonType } from "../Components/Button/Button";
import { ButtonFrameworkVariant } from "../Components/ButtonFramework/ButtonFramework";
import { InfoTooltip } from "../Components/InfoTooltip/InfoTooltip";
import { TestPowerStatus } from "./MetaLiftCreate";
import {
  MdHelpOutline,
  MdClear,
  MdCheck,
  MdCalendarToday,
  MdKeyboardArrowUp,
  MdKeyboardArrowDown,
} from "react-icons/md";
import { formatMoney } from "@blisspointmedia/bpm-types/dist/formatUtils";
import { CellPowerResult } from "./MetaLiftStudyUtils";
import { CampaignCell } from "@blisspointmedia/bpm-types/dist/MetaLiftStudy";

interface MetaLiftTestPowerProps {
  status: TestPowerStatus;
  errorMessage?: string | null;
  onRunTest: () => void;
  isFormValid: boolean;
  isResultsOutdated: boolean;
  isCellToCellSensitivityPowered: boolean;
  neededDailySpend: number;
  historicalDailySpend: number;
  testLengthDays: number;
  minRequiredDurationDays: number;
  cellPowerResults: CellPowerResult[];
  campaignCells: CampaignCell[];
  pairwiseValue: number;
}

const getCellSubtitle = (cellResult: CellPowerResult, campaignCells: CampaignCell[]): string => {
  const matchingCell = campaignCells.find(c => c.id === cellResult.cellId);
  if (!matchingCell || matchingCell.selectedItems.length === 0) {
    return "";
  }
  return matchingCell.selectedItems.map(item => item.name).join(", ");
};

const CellPowerCard: React.FC<{
  cellResult: CellPowerResult;
  subtitle: string;
}> = ({ cellResult, subtitle }) => {
  const { isPowered, cellName, minRequiredBudget, configurationBudget } = cellResult;

  return (
    <div className={`cellPowerCard ${isPowered ? "cellPowerCardSuccess" : "cellPowerCardError"}`}>
      <div className="cellPowerCardHeader">
        <div
          className={`cellPowerCardIcon ${
            isPowered ? "cellPowerCardIconSuccess" : "cellPowerCardIconError"
          }`}
        >
          {isPowered ? <MdCheck size={18} /> : <MdClear size={18} />}
        </div>
        <div className="cellPowerCardInfo">
          <div className="cellPowerCardName">{cellName}</div>
          {subtitle && (
            <div className="cellPowerCardSubtitle" title={subtitle}>
              {subtitle}
            </div>
          )}
        </div>
      </div>
      {!isPowered && (
        <div className="cellPowerCardBudget">
          <div className="cellPowerCardBudgetRow">
            <span className="cellPowerCardBudgetLabel">Min required Budget:</span>
            <span className="cellPowerCardBudgetValue">{formatMoney(minRequiredBudget)}</span>
          </div>
          <div className="cellPowerCardBudgetRow">
            <span className="cellPowerCardBudgetLabel cellPowerCardBudgetLabelError">
              Configuration Budget:
            </span>
            <span className="cellPowerCardBudgetValue cellPowerCardBudgetValueError">
              {formatMoney(configurationBudget)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const SensitivityRow: React.FC<{
  isPowered: boolean;
  neededDailySpend: number;
  historicalDailySpend: number;
  testLengthDays: number;
}> = ({ isPowered, neededDailySpend, historicalDailySpend, testLengthDays }) => (
  <div className={`cellPowerCard ${isPowered ? "cellPowerCardSuccess" : "cellPowerCardError"}`}>
    <div className="cellPowerCardHeader">
      <div
        className={`cellPowerCardIcon ${
          isPowered ? "cellPowerCardIconSuccess" : "cellPowerCardIconError"
        }`}
      >
        {isPowered ? <MdCheck size={18} /> : <MdClear size={18} />}
      </div>
      <div className="cellPowerCardInfo cellPowerCardInfoSensitivity">
        <span className="cellPowerCardName">Cell-to-Cell Sensitivity</span>
        <InfoTooltip>
          Cell-to-Cell Sensitivity measures whether the test can detect meaningful differences
          between cells.
        </InfoTooltip>
      </div>
    </div>
    {!isPowered && (
      <div className="cellPowerCardBudget">
        <div className="cellPowerCardBudgetRow">
          <span className="cellPowerCardBudgetLabel">Min required Budget:</span>
          <span className="cellPowerCardBudgetValue">
            {formatMoney(neededDailySpend * testLengthDays)}
          </span>
        </div>
        <div className="cellPowerCardBudgetRow">
          <span className="cellPowerCardBudgetLabel cellPowerCardBudgetLabelError">
            Configuration Budget:
          </span>
          <span className="cellPowerCardBudgetValue cellPowerCardBudgetValueError">
            {formatMoney(historicalDailySpend * testLengthDays)}
          </span>
        </div>
      </div>
    )}
  </div>
);

const DurationFooter: React.FC<{ minRequiredDurationDays: number }> = ({
  minRequiredDurationDays,
}) => (
  <div className="testPowerDurationBox">
    <MdCalendarToday size={20} className="testPowerDurationIcon" />
    <span className="testPowerDurationText">
      Based on the current test setup, the minimum required duration is {minRequiredDurationDays}{" "}
      days.
    </span>
  </div>
);

const MetaLiftTestPower = ({
  status,
  errorMessage,
  onRunTest,
  isFormValid,
  isResultsOutdated,
  isCellToCellSensitivityPowered,
  neededDailySpend,
  historicalDailySpend,
  testLengthDays,
  cellPowerResults,
  campaignCells,
  minRequiredDurationDays,
}: MetaLiftTestPowerProps): JSX.Element => {
  const [showPoweredCells, setShowPoweredCells] = useState(true);

  const isOverallPowered = useMemo(
    () =>
      cellPowerResults.length > 0 &&
      cellPowerResults.every(c => c.isPowered) &&
      isCellToCellSensitivityPowered,
    [cellPowerResults, isCellToCellSensitivityPowered]
  );

  const hasMultipleCells = cellPowerResults.length > 1;

  const visibleCells = useMemo(
    () => (showPoweredCells ? cellPowerResults : cellPowerResults.filter(c => !c.isPowered)),
    [cellPowerResults, showPoweredCells]
  );

  const renderOutdatedOrNotRunState = (headerText: string) => (
    <div className="metaLiftTestPowerList">
      <div className="metaLiftTestPowerListText">
        Confirm that your current test setup will yield valid results.
      </div>
      <div className="metaLiftTestPowerResult metaLiftTestPowerResultNotRun">
        <div className="metaLiftTestPowerResultHolder">
          <div className="metaLiftTestPowerIconHolder metaLiftTestPowerIconHolderNotRun">
            <MdHelpOutline className="metaLiftTestPowerIconNotRun" />
          </div>
          <div className="metaLiftTestPowerResultTextHeader metaLiftTestPowerResultTextHeaderNotRun">
            {headerText}
          </div>
          {isFormValid ? (
            headerText === "Results Outdated" ? (
              <div className="metaLiftTestPowerResultTextSubtext metaLiftTestPowerResultTextSubtextNotRun">
                Your setup has changed. Re-run the check to update.
              </div>
            ) : (
              <></>
            )
          ) : (
            <div className="metaLiftTestPowerResultTextSubtext metaLiftTestPowerResultTextSubtextNotRun">
              Complete duration, cells, and objectives to run power check.
            </div>
          )}
          <ButtonIcon
            type={ButtonType.FILLED}
            variant={ButtonFrameworkVariant.NO_ICON}
            onClick={onRunTest}
            size="lg"
            className="metaLiftTestPowerRunButton"
            disabled={!isFormValid}
          >
            Run Power Check
          </ButtonIcon>
        </div>
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="metaLiftTestPowerList">
      <div className="metaLiftTestPowerListText">
        Confirm that your current test setup will yield valid results.
      </div>
      <div className="metaLiftTestPowerResult metaLiftTestPowerResultNotWellPowered">
        <div className="metaLiftTestPowerResultHolder">
          <div className="metaLiftTestPowerIconHolder metaLiftTestPowerIconHolderNotWellPowered">
            <MdClear className="metaLiftTestPowerIconNotWellPowered" />
          </div>
          <div className="metaLiftTestPowerResultTextHeader metaLiftTestPowerResultTextHeaderNotWellPowered">
            Power Check Couldn&apos;t Run
          </div>
          <div className="metaLiftTestPowerResultTextSubtext metaLiftTestPowerResultTextSubtextNotWellPowered">
            {errorMessage || "Something went wrong calculating power. Please try again."}
          </div>
          <ButtonIcon
            type={ButtonType.FILLED}
            variant={ButtonFrameworkVariant.NO_ICON}
            onClick={onRunTest}
            size="lg"
            className="metaLiftTestPowerRunButton"
            disabled={!isFormValid}
          >
            Run Power Check
          </ButtonIcon>
        </div>
      </div>
    </div>
  );

  const renderResultsState = () => (
    <div className="metaLiftTestPowerList">
      <div className="metaLiftTestPowerListText">
        Confirm that your current test setup will yield valid results.
      </div>

      {isOverallPowered ? (
        <div className="metaLiftTestPowerResult">
          <div className="metaLiftTestPowerResultHolder">
            <div className="metaLiftTestPowerIconHolder">
              <MdCheck className="metaLiftTestPowerIcon" />
            </div>
            <div className="metaLiftTestPowerResultTextHeader">Well Powered</div>
            <div className="metaLiftTestPowerResultTextSubtext">
              Your test is strong enough to produce reliable results.
            </div>
            <div className="metaLiftTestPowerResultTextLink">Download Details</div>
          </div>
        </div>
      ) : (
        <div className="metaLiftTestPowerResult metaLiftTestPowerResultNotWellPowered">
          <div className="metaLiftTestPowerResultHolder">
            <div className="metaLiftTestPowerIconHolder metaLiftTestPowerIconHolderNotWellPowered">
              <MdClear className="metaLiftTestPowerIconNotWellPowered" />
            </div>
            <div className="metaLiftTestPowerResultTextHeader metaLiftTestPowerResultTextHeaderNotWellPowered">
              Not Powered
            </div>
            <div className="metaLiftTestPowerResultTextSubtext metaLiftTestPowerResultTextSubtextNotWellPowered">
              Adjust your budget then re-run the power check.
            </div>
            <div className="metaLiftTestPowerResultTextLink">Download Details</div>
          </div>
        </div>
      )}

      {isOverallPowered && (
        <div className="metaLiftTestPowerBudgetSection">
          <div className="metaLiftTestPowerBudgetDetails">
            <div className="metaLiftTestPowerBudgetText">Min required Budget:</div>
            <div className="metaLiftTestPowerBudgetTextValue">
              {formatMoney(neededDailySpend * testLengthDays)}
            </div>
          </div>
          <div className="metaLiftTestPowerBudgetDetails">
            <div className="metaLiftTestPowerBudgetText--green">Configuration Budget:</div>
            <div className="metaLiftTestPowerBudgetTextValue--green">
              {formatMoney(historicalDailySpend * testLengthDays)}
            </div>
          </div>
        </div>
      )}

      {visibleCells.map(cellResult => (
        <CellPowerCard
          key={cellResult.cellId}
          cellResult={cellResult}
          subtitle={getCellSubtitle(cellResult, campaignCells)}
        />
      ))}

      {hasMultipleCells && (
        <SensitivityRow
          isPowered={isCellToCellSensitivityPowered}
          neededDailySpend={neededDailySpend}
          historicalDailySpend={historicalDailySpend}
          testLengthDays={testLengthDays}
        />
      )}

      <DurationFooter minRequiredDurationDays={minRequiredDurationDays} />

      <button className="testPowerToggleCells" onClick={() => setShowPoweredCells(prev => !prev)}>
        {showPoweredCells ? "Hide Powered Cells" : "Show Powered Cells"}{" "}
        {showPoweredCells ? <MdKeyboardArrowUp size={18} /> : <MdKeyboardArrowDown size={18} />}
      </button>
    </div>
  );

  return (
    <div className="metaLiftTestPower">
      <div className="metaLiftTestPowerHeader">
        Test Power{" "}
        <InfoTooltip>
          {" "}
          Each test requires at least one cell and can include at most 5 cells. Multiple cells are
          best used to directly test competing strategies against each other, not to evaluate the
          relative performance of different campaigns. For example, if you wanted to evaluate the
          incremental performance of BAU conversion optimized campaigns against revenue optimized
          campaigns, a two-celled test would be the correct option. On the other hand, if you wanted
          to evaluate the incremental performance of prospecting campaigns and retargeting
          campaigns, you should configure two separate one-celled tests.
        </InfoTooltip>
      </div>
      {status === "error" && renderErrorState()}
      {status === "not_run" && renderOutdatedOrNotRunState("No score yet")}
      {status === "well_powered" &&
        (isResultsOutdated
          ? renderOutdatedOrNotRunState("Results Outdated")
          : renderResultsState())}
      {status === "not_well_powered" &&
        (isResultsOutdated
          ? renderOutdatedOrNotRunState("Results Outdated")
          : renderResultsState())}
    </div>
  );
};

export default MetaLiftTestPower;
